import React, { useState } from 'react';
import { Smartphone, Download, Wifi, WifiOff, Bell, Shield, Zap, CheckCircle, Apple, Chrome } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePWAInstall } from '../hooks/usePWAInstall';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';

const MobileAppPage = () => {
  const { canInstall, isInstalled, install } = usePWAInstall();
  const [activeTab, setActiveTab] = useState('ios');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white relative overflow-hidden">
      <SEOHead 
        title="Application Mobile" 
        description="Installez SENDBOX sur votre smartphone iOS ou Android. Application web progressive (PWA) pour partager vos fichiers en mobilité." 
      />
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Header */}
      <NavigationMenu />

      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-24">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-6">
            <Smartphone className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-300">Application Mobile</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            SENDBOX dans <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">votre poche</span>
          </h1>
          <p className="text-blue-300/60 max-w-lg mx-auto text-base sm:text-lg">
            Installez SENDBOX directement sur votre smartphone. Aucun téléchargement depuis un store, aucune mise à jour manuelle.
          </p>
        </div>

        {/* Phone Mockups with animations */}
        <div className="flex justify-center items-center gap-6 sm:gap-10 mb-16">
          {/* Phone 1 */}
          <div className="relative" data-testid="phone-mockup-1" style={{animation: 'float 4s ease-in-out infinite'}}>
            <div className="relative w-[140px] sm:w-[180px] md:w-[200px]">
              {/* Phone shell */}
              <div className="bg-gradient-to-b from-slate-600 via-slate-700 to-slate-800 rounded-[2rem] sm:rounded-[2.5rem] p-[3px] shadow-2xl shadow-blue-500/30">
                <div className="bg-black rounded-[1.85rem] sm:rounded-[2.3rem] p-1 sm:p-1.5">
                  <div className="relative bg-slate-950 rounded-[1.6rem] sm:rounded-[2rem] overflow-hidden">
                    {/* Dynamic Island */}
                    <div className="absolute top-1.5 sm:top-2 left-1/2 transform -translate-x-1/2 z-20">
                      <div className="w-14 sm:w-20 h-4 sm:h-5 bg-black rounded-full flex items-center justify-center gap-1">
                        <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-slate-800 rounded-full"></div>
                      </div>
                    </div>
                    {/* Screen */}
                    <img 
                      src="/images/pwa/screenshot-home.png" 
                      alt="SENDBOX Accueil" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
              {/* Side buttons */}
              <div className="absolute -right-[2px] top-20 sm:top-24 w-[3px] h-8 sm:h-10 bg-slate-600 rounded-l-sm"></div>
              <div className="absolute -left-[2px] top-16 sm:top-20 w-[3px] h-5 sm:h-6 bg-slate-600 rounded-r-sm"></div>
              <div className="absolute -left-[2px] top-24 sm:top-28 w-[3px] h-8 sm:h-10 bg-slate-600 rounded-r-sm"></div>
            </div>
          </div>

          {/* Phone 2 */}
          <div className="relative hidden sm:block" data-testid="phone-mockup-2" style={{animation: 'float 4s ease-in-out infinite', animationDelay: '1.5s'}}>
            <div className="relative w-[180px] md:w-[200px] mt-6">
              {/* Phone shell */}
              <div className="bg-gradient-to-b from-slate-600 via-slate-700 to-slate-800 rounded-[2.5rem] p-[3px] shadow-2xl shadow-cyan-500/30">
                <div className="bg-black rounded-[2.3rem] p-1.5">
                  <div className="relative bg-slate-950 rounded-[2rem] overflow-hidden">
                    {/* Dynamic Island */}
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20">
                      <div className="w-20 h-5 bg-black rounded-full flex items-center justify-center gap-1">
                        <div className="w-2 h-2 bg-slate-800 rounded-full"></div>
                      </div>
                    </div>
                    {/* Screen */}
                    <img 
                      src="/images/pwa/screenshot-features.png" 
                      alt="SENDBOX Fonctionnalités" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
              {/* Side buttons */}
              <div className="absolute -right-[2px] top-24 w-[3px] h-10 bg-slate-600 rounded-l-sm"></div>
              <div className="absolute -left-[2px] top-20 w-[3px] h-6 bg-slate-600 rounded-r-sm"></div>
              <div className="absolute -left-[2px] top-28 w-[3px] h-10 bg-slate-600 rounded-r-sm"></div>
            </div>
          </div>
        </div>

        {/* CSS Animation Keyframes */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
        `}</style>

        {/* Install Button */}
        <div className="text-center mb-16" data-testid="install-section">
          {isInstalled ? (
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-xl px-6 py-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-300 font-medium">SENDBOX est installé sur cet appareil</span>
            </div>
          ) : canInstall ? (
            <Button onClick={install} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 text-base rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all" data-testid="install-pwa-btn">
              <Download className="w-5 h-5 mr-2" /> Installer SENDBOX
            </Button>
          ) : null}
        </div>

        {/* Installation Tutorial */}
        <div className="bg-slate-800/40 border border-blue-500/15 rounded-2xl p-6 sm:p-8 mb-16" data-testid="installation-tutorial">
          <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-8">
            Comment installer SENDBOX ?
          </h2>
          
          {/* Tab buttons */}
          <div className="flex justify-center gap-3 mb-8">
            <button
              onClick={() => setActiveTab('ios')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                activeTab === 'ios' 
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30' 
                  : 'bg-slate-700/50 text-blue-300/70 hover:bg-slate-700'
              }`}
              data-testid="tab-ios"
            >
              <Apple className="w-4 h-4" />
              iPhone / iPad
            </button>
            <button
              onClick={() => setActiveTab('android')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                activeTab === 'android' 
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30' 
                  : 'bg-slate-700/50 text-blue-300/70 hover:bg-slate-700'
              }`}
              data-testid="tab-android"
            >
              <Chrome className="w-4 h-4" />
              Android
            </button>
          </div>

          {/* iOS Tutorial */}
          {activeTab === 'ios' && (
            <div className="space-y-6" data-testid="tutorial-ios">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Step 1 */}
                <div className="bg-slate-900/60 rounded-xl p-5 border border-blue-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 font-bold text-sm">1</div>
                    <h3 className="text-white font-semibold">Ouvrez Safari</h3>
                  </div>
                  <img src="/images/pwa/tutorial-ios-1.png" alt="Étape 1 iOS" className="w-full max-w-[200px] mx-auto rounded-lg mb-4" />
                  <p className="text-blue-300/60 text-sm">
                    Ouvrez SENDBOX dans <span className="text-blue-400 font-medium">Safari</span> (le navigateur natif d'Apple). 
                    Appuyez sur le bouton <span className="text-blue-400 font-medium">Partager</span> (icône carrée avec flèche vers le haut).
                  </p>
                </div>

                {/* Step 2 */}
                <div className="bg-slate-900/60 rounded-xl p-5 border border-blue-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 font-bold text-sm">2</div>
                    <h3 className="text-white font-semibold">Ajoutez à l'écran d'accueil</h3>
                  </div>
                  <img src="/images/pwa/tutorial-ios-2.png" alt="Étape 2 iOS" className="w-full max-w-[200px] mx-auto rounded-lg mb-4" />
                  <p className="text-blue-300/60 text-sm">
                    Dans le menu qui s'affiche, faites défiler et appuyez sur <span className="text-blue-400 font-medium">"Sur l'écran d'accueil"</span>. 
                    Confirmez en appuyant sur "Ajouter".
                  </p>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
                <p className="text-blue-300 text-sm">
                  <span className="font-semibold">Important :</span> L'installation PWA sur iOS ne fonctionne qu'avec Safari. Chrome et Firefox ne supportent pas cette fonctionnalité.
                </p>
              </div>
            </div>
          )}

          {/* Android Tutorial */}
          {activeTab === 'android' && (
            <div className="space-y-6" data-testid="tutorial-android">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Step 1 */}
                <div className="bg-slate-900/60 rounded-xl p-5 border border-blue-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 font-bold text-sm">1</div>
                    <h3 className="text-white font-semibold">Ouvrez Chrome</h3>
                  </div>
                  <img src="/images/pwa/tutorial-android.png" alt="Étape Android" className="w-full max-w-[200px] mx-auto rounded-lg mb-4" />
                  <p className="text-blue-300/60 text-sm">
                    Ouvrez SENDBOX dans <span className="text-blue-400 font-medium">Google Chrome</span>. 
                    Appuyez sur le menu <span className="text-blue-400 font-medium">⋮</span> (trois points verticaux) en haut à droite.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="bg-slate-900/60 rounded-xl p-5 border border-blue-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 font-bold text-sm">2</div>
                    <h3 className="text-white font-semibold">Installez l'application</h3>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                        <Download className="w-5 h-5 text-blue-400" />
                        <span className="text-white text-sm font-medium">Installer l'application</span>
                      </div>
                      <div className="text-blue-300/40 text-xs text-center">ou</div>
                      <div className="flex items-center gap-3 p-2 bg-slate-700/50 rounded-lg">
                        <Smartphone className="w-5 h-5 text-blue-300/60" />
                        <span className="text-blue-300/60 text-sm">Ajouter à l'écran d'accueil</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-blue-300/60 text-sm">
                    Appuyez sur <span className="text-blue-400 font-medium">"Installer l'application"</span> ou <span className="text-blue-400 font-medium">"Ajouter à l'écran d'accueil"</span>. 
                    L'icône SENDBOX apparaîtra sur votre écran d'accueil.
                  </p>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
                <p className="text-green-300 text-sm">
                  <span className="font-semibold">Astuce :</span> Sur Android, vous pouvez aussi utiliser Edge, Samsung Internet, ou Opera pour installer la PWA.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16" data-testid="pwa-features">
          {[
            { icon: <Zap className="w-5 h-5" />, title: 'Lancement instantané', desc: 'S\'ouvre comme une vraie app depuis votre écran d\'accueil.' },
            { icon: <WifiOff className="w-5 h-5" />, title: 'Mode hors connexion', desc: 'Accessible même sans connexion internet.' },
            { icon: <Shield className="w-5 h-5" />, title: 'Sécurisé', desc: 'Chiffrement E2E, mêmes protections que le web.' },
            { icon: <Download className="w-5 h-5" />, title: 'Ultra léger', desc: 'Moins de 1 Mo sur votre appareil.' },
            { icon: <Bell className="w-5 h-5" />, title: 'Toujours à jour', desc: 'Mises à jour automatiques en arrière-plan.' },
            { icon: <Wifi className="w-5 h-5" />, title: 'Fonctionnalités complètes', desc: 'Upload, partage, QR code, mot de passe.' },
          ].map((f, i) => (
            <div key={i} className="bg-slate-800/40 border border-blue-500/15 rounded-xl p-5 hover:border-blue-500/30 transition-all">
              <div className="text-blue-400 mb-3">{f.icon}</div>
              <h3 className="text-white font-semibold text-sm mb-1">{f.title}</h3>
              <p className="text-blue-300/50 text-xs leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="bg-slate-800/30 border border-blue-500/10 rounded-2xl p-6 sm:p-8" data-testid="pwa-faq">
          <h2 className="text-lg font-semibold text-white mb-6">Questions fréquentes</h2>
          <div className="space-y-5">
            {[
              { q: 'Qu\'est-ce qu\'une PWA ?', a: 'Une Progressive Web App est une application web qui s\'installe sur votre appareil comme une app native, mais sans passer par les stores. Elle est plus légère et toujours à jour.' },
              { q: 'Est-ce que ça prend de la place ?', a: 'Très peu. Contrairement à une app native, la PWA n\'occupe que quelques centaines de Ko sur votre appareil.' },
              { q: 'Pourquoi Safari sur iPhone ?', a: 'Apple restreint l\'installation des PWA à Safari uniquement. Chrome et Firefox sur iOS ne peuvent pas installer de PWA.' },
              { q: 'Dois-je créer un compte ?', a: 'Non, vous pouvez utiliser SENDBOX sans compte. Un compte est nécessaire uniquement pour les fonctionnalités Pro.' },
            ].map((item, i) => (
              <div key={i}>
                <h3 className="text-white text-sm font-medium mb-1">{item.q}</h3>
                <p className="text-blue-300/50 text-xs leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default MobileAppPage;
